import { getFullName } from "../careprovider/visits/utils";

const parseMessageBody = (rawMessage) => {
  if (!rawMessage) {
    return {};
  }

  try {
    return JSON.parse(rawMessage);
  } catch {
    return {};
  }
};

const getSenderMember = (senderId, members = []) =>
  members.find(
    (item) => String(item?.userIdentifier) === String(senderId),
  );

const getSenderName = (member, senderId) => {
  if (senderId == null || senderId === "") {
    return "";
  }

  if (!member) {
    return String(senderId);
  }

  return (
    getFullName(member.givenName, member.familyName) ||
    member.displayName ||
    String(senderId)
  );
};

export const buildRoomMemberDirectory = (roomMembers = [], messages = []) => {
  const directory = new Map();

  roomMembers.forEach((member) => {
    const userId = member?.userIdentifier;
    if (!userId) {
      return;
    }

    directory.set(String(userId), member);
  });

  const addMemberFromName = (userId, displayName) => {
    if (!userId || !displayName || directory.has(String(userId))) {
      return;
    }

    directory.set(String(userId), {
      userIdentifier: userId,
      displayName,
    });
  };

  messages.forEach((message) => {
    addMemberFromName(message?.sender, message?.senderName);

    message?.reactions?.forEach((reaction) => {
      addMemberFromName(reaction?.userIdentifier, reaction?.userName);
    });
  });

  return [...directory.values()];
};

export const REACTION_OPTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🎉", "😊", "👏", "😍", "🤔", "💯", "😎", "😭", "🤝", "✅", "👌", "🚀", "💪"];

const parseRecipientMetadata = (metadata) => {
  if (!metadata) {
    return {};
  }

  if (typeof metadata === "object") {
    return metadata;
  }

  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
};

const parseReactionsFromMessage = (item, members = []) => {
  if (!Array.isArray(item?.recipients)) {
    return [];
  }

  return item.recipients
    .map((recipient) => {
      const userIdentifier = String(
        recipient?.recipient
      );
      if (!userIdentifier || recipient?.metadata == null) {
        return null;
      }

      const metadata = parseRecipientMetadata(recipient.metadata);
      const reaction = metadata?.reaction;
      if (!reaction) {
        return null;
      }

      const member = getSenderMember(userIdentifier, members);

      return {
        userIdentifier,
        reaction,
        userName: getSenderName(member, userIdentifier),
      };
    })
    .filter(Boolean);
};

export const groupReactions = (reactions = []) => {
  const grouped = new Map();

  reactions.forEach(({ userIdentifier, reaction, userName }) => {
    if (!reaction || !userIdentifier) {
      return;
    }

    const userId = String(userIdentifier);
    if (!grouped.has(reaction)) {
      grouped.set(reaction, []);
    }

    const users = grouped.get(reaction);
    if (!users.some((user) => String(user.userIdentifier) === userId)) {
      users.push({
        userIdentifier: userId,
        userName: userName || userId,
      });
    }
  });

  return [...grouped.entries()].map(([reaction, users]) => ({
    reaction,
    users,
    count: users.length,
    namesLabel: users.map((user) => user.userName).join(", "),
  }));
};

export const applyUserReaction = (
  reactions = [],
  userIdentifier,
  reaction,
  userName,
) => {
  const userId = String(userIdentifier);
  const withoutUser = reactions.filter(
    (entry) => String(entry.userIdentifier) !== userId,
  );

  if (reaction == null) {
    return withoutUser;
  }

  return [
    ...withoutUser,
    {
      userIdentifier: userId,
      reaction,
      userName: userName || userId,
    },
  ];
};

export const upsertMessageReactionInRoomMap = (
  messagesByRoom,
  roomId,
  messageId,
  userIdentifier,
  reaction,
  userName,
) => {
  if (!roomId || !messageId || !userIdentifier) {
    return messagesByRoom;
  }

  const roomMessages = messagesByRoom[roomId];
  if (!roomMessages?.length) {
    return messagesByRoom;
  }

  const index = roomMessages.findIndex(
    (message) => String(message.messageId) === String(messageId),
  );

  if (index === -1) {
    return messagesByRoom;
  }

  const updated = [...roomMessages];
  updated[index] = {
    ...updated[index],
    reactions: applyUserReaction(
      updated[index].reactions,
      userIdentifier,
      reaction,
      userName,
    ),
  };

  return {
    ...messagesByRoom,
    [roomId]: updated,
  };
};

const getRawMessageContent = (item) => {
  const body = parseMessageBody(item?.message);
  if (body.chatMessage || body.message) {
    return body.chatMessage || body.message;
  }

  if (typeof item?.message === "string" && item.message.trim()) {
    return item.message;
  }

  return "";
};

export const formatRoomInfoContent = (
  content,
  members = [],
  currentUserId,
) => {
  if (!content) {
    return "";
  }

  return content.replace(/\{([^}]+)\}/g, (match, userId) => {
    const normalizedId = String(userId).trim();
    if (!normalizedId) {
      return match;
    }

    if (String(normalizedId) === String(currentUserId)) {
      return "You";
    }

    return getSenderName(getSenderMember(normalizedId, members), normalizedId);
  });
};

export const formatMessageDateTime = (timestamp) => {
  if (!timestamp) {
    return "";
  }

  const date = new Date(timestamp);
  const datePart = date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${datePart}, ${timePart}`;
};

const hasValidMessageId = (messageId) =>
  Boolean(messageId) && messageId !== "undefined" && messageId !== "null";

export const isDisplayableChatMessage = (message) =>
  hasValidMessageId(message?.messageId) && Boolean(message?.content?.trim());

export const upsertMessageInRoomMap = (messagesByRoom, roomId, normalizedMessage) => {
  if (
    !roomId ||
    !normalizedMessage?.messageId ||
    normalizedMessage.messageId === "undefined" ||
    normalizedMessage.messageId === "null"
  ) {
    return messagesByRoom;
  }

  const existing = messagesByRoom[roomId] || [];
  const index = existing.findIndex(
    (item) => item.messageId === normalizedMessage.messageId,
  );

  if (index >= 0) {
    const merged = mergeChatMessages(existing[index], normalizedMessage);
    const reactionsChanged =
      JSON.stringify(merged.reactions) !==
      JSON.stringify(existing[index].reactions);

    if (!isDisplayableChatMessage(merged) && !reactionsChanged) {
      return messagesByRoom;
    }

    const updated = [...existing];
    updated[index] = merged;
    return { ...messagesByRoom, [roomId]: updated };
  }

  if (!isDisplayableChatMessage(normalizedMessage)) {
    return messagesByRoom;
  }

  return {
    ...messagesByRoom,
    [roomId]: [...existing, normalizedMessage],
  };
};

export const buildSentMessage = ({
  response,
  trimmedMessage,
  sender,
  roomMembers = [],
  isEdited = false,
}) => {
  const normalized = normalizeApiMessage(response, roomMembers);

  if (normalized?.content?.trim()) {
    return {
      ...normalized,
      edited: isEdited || normalized.edited,
    };
  }

  if (response?.id == null || response?.id === "") {
    return null;
  }

  const senderMember = getSenderMember(sender?.userIdentifier, roomMembers);

  return {
    messageId: String(response.id),
    sender: response?.sender ?? sender?.userIdentifier,
    senderName: getSenderName(senderMember, response?.sender ?? sender?.userIdentifier),
    jobTitle: senderMember?.jobTitle ?? sender?.jobTitle ?? null,
    content: trimmedMessage,
    timestamp: response?.createdAt || response?.updatedAt || Date.now(),
    createdAt: response?.createdAt ?? null,
    updatedAt: response?.updatedAt ?? null,
    edited: isEdited,
    roomInfo: false,
    reactions: [],
  };
};

export const mergeChatMessages = (existing, incoming) => {
  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  return {
    ...existing,
    ...incoming,
    content: incoming.content?.trim() ? incoming.content : existing.content,
    sender: incoming.sender != null ? incoming.sender : existing.sender,
    senderName: incoming.senderName?.trim()
      ? incoming.senderName
      : existing.senderName,
    jobTitle: incoming.jobTitle ?? existing.jobTitle,
    reactions: Array.isArray(incoming.reactions)
      ? incoming.reactions
      : existing.reactions,
    edited: incoming.edited ?? existing.edited,
    roomInfo: incoming.roomInfo ?? existing.roomInfo,
    timestamp: incoming.timestamp || existing.timestamp,
  };
};

export const normalizeApiMessage = (item, members = []) => {
  if (item?.id == null || item?.id === "") {
    return null;
  }

  const senderMember = getSenderMember(item?.sender, members);
  const messageMetadata = parseRecipientMetadata(item?.metadata);
  const roomInfo = Boolean(item?.roomInfo ?? messageMetadata?.roomInfo);

  return {
    messageId: String(item.id),
    sender: item?.sender,
    senderName: getSenderName(senderMember, item?.sender),
    jobTitle: senderMember?.jobTitle || null,
    content: getRawMessageContent(item),
    timestamp: item?.createdAt || item?.updatedAt || Date.now(),
    createdAt: item?.createdAt ?? null,
    updatedAt: item?.updatedAt ?? null,
    edited: Boolean(messageMetadata?.edited),
    roomInfo,
    reactions: roomInfo ? [] : parseReactionsFromMessage(item, members),
  };
};

export const normalizeApiMessages = (items, room) => {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }

  const members = room?.members || [];

  return [...items]
    .sort((left, right) => (left?.createdAt || 0) - (right?.createdAt || 0))
    .map((item) => normalizeApiMessage(item, members))
    .filter(Boolean);
};

/** Prepends older messages without duplicating IDs; both arrays must be chronological. */
export const prependOlderMessages = (existing = [], older = []) => {
  if (!older.length) {
    return existing;
  }

  if (!existing.length) {
    return older;
  }

  const existingIds = new Set(existing.map((message) => String(message.messageId)));
  const uniqueOlder = older.filter(
    (message) => !existingIds.has(String(message.messageId)),
  );

  if (!uniqueOlder.length) {
    return existing;
  }

  return [...uniqueOlder, ...existing];
};

/**
 * Normalizes a message payload from a STOMP event.
 * Accepts either a wrapped `{ message }` object or a raw API message shape.
 */
export const normalizeSocketMessage = (payload, room) => {
  const item = payload?.message || payload;
  const members = room?.members || [];

  return normalizeApiMessage(item, members);
};
